import React, { ReactNode } from "react";
import { FieldValues, useController, UseControllerProps } from "react-hook-form";
import {
  Checkbox as AntCheckbox,
  DatePicker as AntDatePicker,
  DatePickerProps as AntDatePickerProps,
  Input as AntInput,
  Radio as AntRadio,
  Select as AntSelect,
  Space,
  TreeSelect as AntTreeSelect,
} from "antd5";
import { CheckboxProps as AntCheckboxProps } from "antd5/lib/checkbox";
import { InputProps as AntInputProps, TextAreaProps as AntTextArea } from "antd5/lib/input";
import { DefaultOptionType, SelectProps as AntSelectProps } from "antd5/lib/select";
import { TreeSelectProps as AntTreeSelectProps } from "antd5/lib/tree-select";
import { isArray } from "chart.js/helpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { isoStringToDate } from "components/record_search/types";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import Tooltip from "components/ui/tooltip/Tooltip";
import DateRangePicker from "./DateRangePicker";

import css from "./Inputs.module.scss";

dayjs.extend(utc);

type InputType<T> = {
  label: ReactNode;
  subLabel?: string;
  onChange?: (val: T) => void;
  tooltipText?: string;
  showErrors?: boolean;
};

export type Props<T extends FieldValues> = UseControllerProps<T> & InputType<T>;

type InputProps<T extends FieldValues> = Props<T> & AntInputProps & { containerClassName?: string };

export function Input<T extends FieldValues>(props: InputProps<T>) {
  const { label, subLabel, tooltipText, required, containerClassName, ...inputProps } = props;
  const { field, fieldState } = useController(props);

  const input = (
    <AntInput
      {...inputProps}
      {...field}
      id={props.name}
      status={fieldState.error && "error"}
      onChange={(val) => {
        props.onChange?.(val.target.value as unknown as T);
        field.onChange(val);
      }}
      onBlur={(event) => {
        props.onBlur?.(event);
        field.onBlur();
      }}
    />
  );

  return (
    <div className={containerClassName}>
      <label htmlFor={props.name} className={css.label}>
        <h3>
          {required && <span className={css.required}>*</span>}
          {label}
        </h3>
        {subLabel && <span className={css.subLabel}>{subLabel}</span>}
      </label>
      {tooltipText ? withFieldTooltip(input, tooltipText) : input}
      {fieldState.error?.message && (
        <span className={css.errorMessage}>{fieldState.error.message}</span>
      )}
    </div>
  );
}

/**
 * Only wrap when there is tooltip text: the span is needed because antd's inputs don't
 * pass the tooltip's pointer handlers through, but it also becomes the layout item in
 * the caller's container, so every field that has no tooltip is left untouched.
 */
function withFieldTooltip(fieldInput: React.ReactElement, tooltipText: string) {
  return (
    <Tooltip content={tooltipText} side="top" contentMaxWidth={500}>
      <span className={css.tooltipTrigger}>{fieldInput}</span>
    </Tooltip>
  );
}

type TextAreaProps<T extends FieldValues> = Props<T> & AntTextArea;

export function TextArea<T extends FieldValues>(props: TextAreaProps<T>) {
  const { label, subLabel, tooltipText, showErrors = true, ...inputProps } = props;
  const { field, fieldState } = useController(props);

  const textArea = (
    <AntInput.TextArea
      {...inputProps}
      {...field}
      id={props.name}
      status={showErrors ? fieldState.error && "error" : undefined}
      onChange={(val) => {
        props.onChange?.(val.target.value as unknown as T);
        field.onChange(val);
      }}
      onBlur={(event) => {
        props.onBlur?.(event);
        field.onBlur();
      }}
    />
  );

  return (
    <div className={props.className}>
      <label htmlFor={props.name} className={css.label}>
        <h3>{label}</h3>
        {subLabel && <span className={css.subLabel}>{subLabel}</span>}
      </label>
      {tooltipText ? withFieldTooltip(textArea, tooltipText) : textArea}
      {showErrors && fieldState.error?.message && (
        <span className={css.errorMessage}>{fieldState.error.message}</span>
      )}
    </div>
  );
}

export type SelectProps<
  T extends FieldValues,
  OptionType extends DefaultOptionType = DefaultOptionType,
> = Omit<Props<T>, "onChange"> &
  AntSelectProps & {
    options: OptionType[];
    selectAll?: boolean;
    required?: boolean;
    preventFormValueChange?: boolean;
    onChange?: (val: T, opt?: OptionType | OptionType[]) => void;

    /**
     * @deprecated Avoid using a portal to render the dropdown.
     * Instead, try to ensure the parent container does not use overflow styling (e.g., overflow: hidden)
     * so the dropdown can be rendered and positioned correctly without needing a portal.
     */
    enablePortal?: boolean;
  };

export function Select<
  T extends FieldValues,
  OptionType extends DefaultOptionType = DefaultOptionType,
>(props: SelectProps<T, OptionType>) {
  const { field, fieldState } = useController(props);
  const {
    label,
    subLabel,
    selectAll,
    required,
    preventFormValueChange,
    // Extract UseControllerProps to prevent them from being passed as HTML attributes
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    control,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    name,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rules,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    defaultValue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    shouldUnregister,
    ...inputProps
  } = props;

  let options = props.options;
  const fieldValue: OptionType["value"][] = Array.isArray(field.value)
    ? (field.value as OptionType["value"][])
    : [];
  const selectedValues = new Set<OptionType["value"]>(fieldValue);
  if (selectAll && props.options.length > 0) {
    // check if we have all the options selected already
    if (
      fieldValue.length === 0 ||
      !props.options.every((option) => selectedValues.has(option.value))
    ) {
      const optionswithSelectAll: OptionType[] = [
        {
          label: <span className={css.addAllSelectOption}>Add {options.length} matching</span>,
          value: "ALL",
        } as OptionType,
        ...props.options,
      ];
      options = optionswithSelectAll;
    }
  }

  return (
    <div className={css.flexColumn}>
      {(label || subLabel) && (
        <label htmlFor={props.name} className={css.label}>
          {label && (
            <h3>
              {required && <span className={css.required}>*</span>}
              {label}
            </h3>
          )}
          {subLabel && <span className={css.subLabel}>{subLabel}</span>}
        </label>
      )}
      <AntSelect
        id={props.name}
        getPopupContainer={(node) => (props.enablePortal ? document.body : node)}
        {...inputProps}
        {...field}
        status={fieldState.error && "error"}
        onChange={(val, opt) => {
          let value: typeof val = val;
          if (selectAll && isArray(val) && val.includes("ALL")) {
            const filtered = val.filter((v: unknown) => v !== "ALL");
            const optionValues = props.options.map((option) => option.value);
            const combined = [...filtered, ...optionValues];
            value = [...new Set(combined)] as typeof val;
          }
          props.onChange?.(value as unknown as T, opt);
          // TODO: this should be removed as these components are meant for use within forms
          if (!preventFormValueChange) {
            field.onChange(value);
          }
        }}
        options={options}
        notFoundContent={
          props.loading ? (
            <span className={css.loadingSkeleton}>
              <SkeletonText width="100%" />
              <SkeletonText width="100%" />
              <SkeletonText width="100%" />
            </span>
          ) : (
            props.notFoundContent
          )
        }
        onBlur={(event) => {
          props.onBlur?.(event);
          field.onBlur();
        }}
      />
      {fieldState.error?.message && (
        <span className={css.errorMessage}>{fieldState.error.message}</span>
      )}
    </div>
  );
}

export type TreeSelectProps<T extends FieldValues> = Props<T> &
  AntTreeSelectProps & { treeData: AntTreeSelectProps["treeData"] };

export function TreeSelect<T extends FieldValues>(props: TreeSelectProps<T>) {
  const { field, fieldState } = useController(props);
  const { label, subLabel, ...inputProps } = props;

  return (
    <div className={css.flexColumn}>
      <label htmlFor={props.name} className={css.label}>
        <h3>{label}</h3>
        {subLabel && <span className={css.subLabel}>{subLabel}</span>}
      </label>
      <AntTreeSelect
        {...inputProps}
        {...field}
        id={props.name}
        // only set the value if we have data otherwise it will error
        // value={props.treeData?.length ? field.value : []}
        value={props.treeData && props.treeData?.length > 0 ? field.value : []}
        // An explicit `status` wins, so a caller can surface a fetch failure that react-hook-form
        // knows nothing about. Falls back to the validation error when unset.
        status={props.status ?? (fieldState.error && "error")}
        onChange={(val) => {
          props.onChange?.(val as unknown as T);
          field.onChange(val);
        }}
        onBlur={(event) => {
          props.onBlur?.(event);
          field.onBlur();
        }}
        treeData={props.treeData}
      />
      {fieldState.error?.message && (
        <span className={css.errorMessage}>{fieldState.error.message}</span>
      )}
    </div>
  );
}

type RadioProps<T extends FieldValues> = Props<T> & {
  options: { label: ReactNode; value: string | number | boolean }[];
  optionType?: "default" | "button";
  direction?: "vertical" | "horizontal";
  disabled?: boolean;
  className?: string;
};

export function Radio<T extends FieldValues>(props: RadioProps<T>) {
  const { field, fieldState } = useController(props);

  return (
    <div className={css.flexColumn}>
      <label htmlFor={props.name} className={css.label}>
        <h3>{props.label}</h3>
        {props.subLabel && <span className={css.subLabel}>{props.subLabel}</span>}
      </label>
      <AntRadio.Group
        {...field}
        optionType={props.optionType || "button"}
        onChange={(val) => {
          props.onChange?.(val.target.value as unknown as T);
          field.onChange(val);
        }}
      >
        <Space.Compact block direction={props.direction || "horizontal"} className={css.space}>
          {props.options.map((option, index) => (
            <AntRadio
              value={option.value}
              key={index}
              disabled={props.disabled}
              className={props.className}
            >
              {option.label}
            </AntRadio>
          ))}
        </Space.Compact>
      </AntRadio.Group>
      {fieldState.error?.message && (
        <span className={css.errorMessage}>{fieldState.error.message}</span>
      )}
    </div>
  );
}

type CheckboxProps<T extends FieldValues> = Props<T> &
  AntCheckboxProps & {
    fieldLabel: string;
  };

export function Checkbox<T extends FieldValues>(props: CheckboxProps<T>) {
  const { field, fieldState } = useController(props);

  return (
    <div className={css.flexColumn}>
      <label htmlFor={props.name} className={css.label}>
        <h3>{props.label}</h3>
        {props.subLabel && <span className={css.subLabel}>{props.subLabel}</span>}
      </label>
      <AntCheckbox
        {...field}
        id={props.name}
        checked={field.value}
        onChange={(val) => {
          props.onChange?.(val.target.value as unknown as T);
          field.onChange(val);
        }}
      >
        {props.fieldLabel}
      </AntCheckbox>
      {fieldState.error?.message && (
        <span className={css.errorMessage}>{fieldState.error.message}</span>
      )}
    </div>
  );
}

type DatePickerProps<T extends FieldValues> = AntDatePickerProps &
  Props<T> & {
    showTime?: boolean;
  };
/** @deprecated Replace with components/ui/input/DateInput */
export function DatePicker<T extends FieldValues>(props: DatePickerProps<T>) {
  const { field, fieldState } = useController(props);
  return (
    <div className={css.flexColumn}>
      {(props.label || props.subLabel) && (
        <label htmlFor={props.name} className={css.label}>
          {props.label && <h3>{props.label}</h3>}
          {props.subLabel && <span className={css.subLabel}>{props.subLabel}</span>}
        </label>
      )}
      <AntDatePicker
        showTime={props.showTime}
        placeholder={props.placeholder}
        format={props.format}
        {...field}
        id={props.name}
        value={field.value ? dayjs.utc(field.value) : null}
        onChange={(val) => {
          if (!val) {
            props.onChange?.(null as unknown as T);
            field.onChange(null);
            return;
          }
          // Format the date as YYYY-MM-DD and parse it in UTC mode to avoid time zone shift
          const utcDate = dayjs.utc(val.format("YYYY-MM-DD"), "YYYY-MM-DD").toDate();
          props.onChange?.(utcDate as unknown as T);
          field.onChange(utcDate);
        }}
        disabled={props.disabled}
        disabledDate={props.disabledDate}
        getPopupContainer={(node) => node}
      />
      {fieldState.error?.message && (
        <span className={css.errorMessage}>{fieldState.error.message}</span>
      )}
    </div>
  );
}

type DateFilterProps<T extends FieldValues> = Props<T> & {
  onChange?: (val: DateFilter) => void;
  showFutureDates?: boolean;
};

export type DateFilter = {
  from?: string;
  relativeFrom?: string;
  to?: string;
  relativeTo?: string;
} & FieldValues;
export function DateRange<T extends FieldValues>(props: DateFilterProps<T>) {
  const { field, fieldState } = useController(props);

  const value: DateFilter = { ...field.value };
  const fieldWihoutRef = { ...field, ref: undefined };
  return (
    <div className={css.flexColumn}>
      <label htmlFor={props.name} className={css.label}>
        <h3>{props.label}</h3>
        {props.subLabel && <span className={css.subLabel}>{props.subLabel}</span>}
      </label>
      <DateRangePicker
        {...fieldWihoutRef}
        values={{
          ...value,
          from: isoStringToDate(value?.from),
          to: isoStringToDate(value?.to),
        }}
        handleChange={(dates) => {
          props.onChange?.({
            from: dates.relativeFrom
              ? dates.from?.toISOString()
              : dates.from?.startOf("day").toISOString(),
            to: dates.to?.toISOString(),
            relativeTo: dates.relativeTo,
            relativeFrom: dates.relativeFrom,
          } as unknown as T);
          field.onChange({
            from: dates.relativeFrom
              ? dates.from?.toISOString()
              : dates.from?.startOf("day").toISOString(),
            to: dates.to?.toISOString(),
            relativeTo: dates.relativeTo,
            relativeFrom: dates.relativeFrom,
          });
        }}
        showFutureDates={props.showFutureDates}
        disabled={props.disabled}
      />
      {fieldState.error?.message && (
        <span className={css.errorMessage}>{fieldState.error.message}</span>
      )}
    </div>
  );
}
